/**
 * @file utils.ts
 * @description This file contains the utility functions for the FeatureExperiments module
 * specifically for grouping and ungrouping data in grouped experiment tables
 */

import { GroupedDataRow } from "./types";
import {
  CompanyExperiment,
  SpecialUser,
  UserExperiment,
} from "@blisspointmedia/bpm-types/dist/FeatureExperiments";
import * as R from "ramda";
import * as Dfns from "date-fns/fp";

// CONSTANTS
export const MAX_SHOWN_GROUPED_USER_EMAILS = 3;
export const MAX_SHOWN_GROUPED_COMPANY_NAMES = 3;
export const EMPTY_CELL_TEXT = "-";
export const EXPERIMENT_ROW_HEIGHT = 48;

/**
 * Formats a date string to mm/dd/yyyy hh:mm
 */

export const formatExperimentsDate = (date: string): string => {
  try {
    return R.pipe(Dfns.parseISO, Dfns.format("MM/dd/yyyy hh:mm"))(date);
  } catch (e) {
    console.error("error", e);
    return EMPTY_CELL_TEXT;
  }
};

/**
 * Creates a unique row ID by joining the primary keys with a pipe character.
 *
 * @param primaryKeys - Array of primary keys to join
 * @returns A string representing the unique row ID
 */
export const makeRowId = (primaryKeys: string[]): string => {
  if (!primaryKeys) {
    return "";
  }

  return primaryKeys.join("|");
};

/**
 * Groups flat data by a specified column and aggregates values from another column into arrays.
 *
 * @param data - Array of flat data rows
 * @param groupByColumn - Column to group by
 * @param groupedColumn - Column to aggregate into arrays
 * @returns Array of grouped rows with aggregated data
 */

export const getSpecialUsersByGroup = (
  userGroup: string,
  specialUsers: SpecialUser[]
): SpecialUser[] => {
  return R.filter((specialUser: SpecialUser) => specialUser.user_group === userGroup, specialUsers);
};

export const groupData = <
  T extends UserExperiment | CompanyExperiment | SpecialUser,
  K extends keyof T
>(
  data: T[],
  groupByColumn: keyof T,
  groupedColumn: keyof T
): GroupedDataRow<T, K>[] => {
  if (data.length === 0) {
    return [];
  }

  // Step 1: Group data by the groupByColumn using a Map<string, T[]>
  const groupedMap = new Map<string, T[]>();
  data.forEach(row => {
    const groupKey = String(row[groupByColumn]);
    if (!groupedMap.has(groupKey)) {
      groupedMap.set(groupKey, []);
    }
    groupedMap.get(groupKey)!.push(row);
  });

  // Step 2: Transform Map entries and process each group
  const groupedEntries = Array.from(groupedMap.entries());
  return groupedEntries.map(([groupKey, groupRows]) => {
    // Steps 3 & 4: Single iteration to aggregate values and find latest row
    // We will use the latest row to populate the metadata columns like lastmodified, lastuser, etc.
    let latestRow = groupRows[0];

    // Grouped values are all the experiments in a single group
    // So it's type is an array of the type of that column we are grouping by
    const groupedValues: T[typeof groupedColumn][] = [];

    groupRows.forEach(row => {
      // Collect grouped values
      groupedValues.push(row[groupedColumn]);

      // Track latest row (if lastmodified can be compared)
      if ("lastmodified" in row && "lastmodified" in latestRow) {
        const latestDate = (latestRow as T & { lastmodified: string }).lastmodified as string;
        const currentDate = (row as T & { lastmodified: string }).lastmodified as string;
        if (currentDate && (!latestDate || new Date(currentDate) > new Date(latestDate))) {
          latestRow = row;
        }
      }
    });

    // Step 5: Create grouped rows with unique IDs and return
    const groupedRow = {
      ...latestRow, // Use the latest row as base (includes lastmodified, lastuser, etc.)
      [groupByColumn]: groupKey,
      [groupedColumn]: groupedValues,
      id: makeRowId([groupKey, ...groupedValues.map(String)]),
    };

    // Unknown type assertion should be safe because we're explicitly constructing
    // the grouped row with the correct structure
    return (groupedRow as unknown) as GroupedDataRow<T, K>;
  });
};

/**
 * Ungroups grouped data back to flat format.
 *
 * @param groupedData - Array of grouped data rows
 * @param groupByColumn - Column that was used for grouping
 * @param groupedColumn - Column that was aggregated
 * @returns Array of flat data rows
 */
export const ungroupData = <
  T extends UserExperiment | CompanyExperiment | SpecialUser,
  K extends keyof T
>(
  groupedData: GroupedDataRow<T, K>[],
  groupByColumn: keyof T,
  groupedColumn: K,
  originalExperimentMap: Record<string, T & { id: string }>,
  createNewRow: (groupKey: string, groupedItem: string) => T
): T[] => {
  if (!groupedData || groupedData.length === 0) {
    return [];
  }

  // For each of the grouped values, see if it has an original
  // If it does, add that, if not, it's new so make a new entry
  const flatRows: T[] = [];
  groupedData.forEach(groupedRow => {
    const groupedValues = groupedRow[groupedColumn as keyof GroupedDataRow<T, K>] as T[K][];

    groupedValues.forEach(groupedItem => {
      const rowId = makeRowId([
        String(groupedRow[groupByColumn as keyof GroupedDataRow<T, K>]),
        String(groupedItem),
      ]);

      const original = originalExperimentMap[rowId];
      if (original) {
        const { id, ...originalWithoutId } = original;
        // We know that Omit<T & {id: string}, "id"> is just T, but TypeScript is getting confused
        // which is why we do the unknown assertion here
        flatRows.push((originalWithoutId as unknown) as T);
      } else {
        const newRow = createNewRow(
          String(groupedRow[groupByColumn as keyof GroupedDataRow<T, K>]),
          String(groupedItem)
        );
        flatRows.push(newRow);
      }
    });
  });

  return flatRows;
};
